import { CoreDesiredOutcome } from "@handshake/core-types";
import type { CoreCreateDisputeDto } from "@handshake/core-types";
import type { Observable } from "rxjs";

export interface CoreDesiredOutcomeOption {
  value: CoreDesiredOutcome;
  label: string;
  requiresReason: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
}

export interface CoreRaiseDisputeModalConfig {
  projectMilestoneId: string;
  desiredOutcomeOptions: CoreDesiredOutcomeOption[];
  /** Host provides the HTTP call; component handles loading state and modal close/error. */
  submitRequest: (data: CoreCreateDisputeDto, files: File[]) => Observable<unknown>;
}

export const CORE_BUSINESS_DISPUTE_CONFIG: CoreDesiredOutcomeOption[] = [
  {
    value: CoreDesiredOutcome.CLIENT_RELEASE_FUNDS,
    label: "Client to release funds",
    requiresReason: true,
    reasonLabel: "What amount is being held back and what is the reason the client is giving?",
    reasonPlaceholder: "State your Desired Outcome",
  },
  {
    value: CoreDesiredOutcome.OTHER,
    label: "Other",
    requiresReason: true,
    reasonLabel: "State your Desired Outcome",
    reasonPlaceholder: "Be clear on your desired outcome and justifications",
  },
];

export const CORE_CLIENT_DISPUTE_CONFIG: CoreDesiredOutcomeOption[] = [
  {
    value: CoreDesiredOutcome.REVISION_REWORK,
    label: "Revision / Rework requested (work needs changes)",
    requiresReason: true,
    reasonLabel: "What needs to be changed?",
    reasonPlaceholder: "Describe what revisions or rework is needed",
  },
  {
    value: CoreDesiredOutcome.PARTIAL_REFUND,
    label: "Partial refund (for incomplete or unsatisfactory work)",
    requiresReason: true,
    reasonLabel: "What is the requested refund amount and why?",
    reasonPlaceholder: "State the refund amount and explain why a partial refund is justified",
  },
  {
    value: CoreDesiredOutcome.FULL_REFUND,
    label: "Full refund (work does not meet requirements)",
    requiresReason: true,
    reasonLabel: "What is the requested refund amount and why?",
    reasonPlaceholder: "State the refund amount and explain why a full refund is justified",
  },
  {
    value: CoreDesiredOutcome.OTHER,
    label: "Other (specify your desired outcome)",
    requiresReason: true,
    reasonLabel: "State your Desired Outcome",
    reasonPlaceholder: "Be clear on your desired outcome and justifications",
  },
];

/**
 * Merged desired-outcome options for the dispute detail view so all outcome types resolve (client + business).
 * For OTHER we use the label from the dispute creator's config so "Other" vs "Other (specify your desired outcome)" is correct.
 */
export function getMergedDesiredOutcomeOptionsForDisputeDetail(
  createdByRole: "client" | "business"
): CoreDesiredOutcomeOption[] {
  const clientOther = CORE_CLIENT_DISPUTE_CONFIG.find((o) => o.value === CoreDesiredOutcome.OTHER);
  const businessOther = CORE_BUSINESS_DISPUTE_CONFIG.find((o) => o.value === CoreDesiredOutcome.OTHER);
  const otherOption = createdByRole === "client" ? clientOther : businessOther;
  return [
    ...CORE_CLIENT_DISPUTE_CONFIG.filter((o) => o.value !== CoreDesiredOutcome.OTHER),
    ...CORE_BUSINESS_DISPUTE_CONFIG.filter((o) => o.value !== CoreDesiredOutcome.OTHER),
    ...(otherOption ? [otherOption] : []),
  ];
}
